import React, { useState } from 'react';
import { X, Plus } from '@phosphor-icons/react';
import { toast } from 'sonner';

const DAYS = [['mon', 'Monday'], ['tue', 'Tuesday'], ['wed', 'Wednesday'], ['thu', 'Thursday'], ['fri', 'Friday'], ['sat', 'Saturday'], ['sun', 'Sunday']];

function VenueChips({ label, color, list, setList, placeholder, testid }) {
  const [input, setInput] = useState('');
  const add = () => { const v = input.trim(); if (v && !list.includes(v)) setList([...list, v]); setInput(''); };
  return (
    <div>
      <label className="text-xs font-medium text-muted-foreground">{label}</label>
      <div className="flex gap-1.5 flex-wrap mt-1 mb-1.5">
        {list.map(v => (
          <span key={v} className={`text-xs px-2 py-1 rounded-full flex items-center gap-1 ${color}`}>
            {v}<button type="button" onClick={() => setList(list.filter(x => x !== v))}><X size={11} /></button>
          </span>
        ))}
      </div>
      <div className="flex gap-1.5">
        <input value={input} onChange={e => setInput(e.target.value)} onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); add(); } }}
          placeholder={placeholder} data-testid={testid} className="flex-1 px-3 py-1.5 rounded-lg border border-border bg-background text-xs" />
        <button type="button" onClick={add} className="px-2.5 rounded-lg border border-border"><Plus size={13} /></button>
      </div>
    </div>
  );
}

export const WeekendPrefsDialog = ({ api, settings, onClose, onSaved }) => {
  const [form, setForm] = useState({
    ai_brief: settings.ai_brief || '',
    max_outings: settings.max_outings || 0,
    preferred_start: settings.preferred_start || '',
    travel_radius: ['anywhere', 'south_mumbai'].includes(settings.travel_radius) ? settings.travel_radius : 'custom',
    travel_custom: ['anywhere', 'south_mumbai'].includes(settings.travel_radius) ? '' : (settings.travel_radius || ''),
    eating_out_ok: settings.eating_out_ok !== false,
    auto_day: settings.auto_day || 'thu',
    auto_hour: settings.auto_hour ?? 18,
    surprise_monthly: settings.surprise_monthly !== false,
  });
  const [favs, setFavs] = useState(settings.favourite_venues || []);
  const [blocked, setBlocked] = useState(settings.blocked_venues || []);
  const [saving, setSaving] = useState(false);

  const save = async () => {
    setSaving(true);
    try {
      await api.put('/weekend/settings', {
        ai_brief: form.ai_brief,
        max_outings: Number(form.max_outings),
        preferred_start: form.preferred_start,
        travel_radius: form.travel_radius === 'custom' ? (form.travel_custom || 'anywhere') : form.travel_radius,
        eating_out_ok: form.eating_out_ok,
        favourite_venues: favs, blocked_venues: blocked,
        auto_day: form.auto_day, auto_hour: Number(form.auto_hour),
        surprise_monthly: form.surprise_monthly,
      });
      toast.success('Preferences saved — next plans will follow them');
      onSaved();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to save'); }
    setSaving(false);
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-card rounded-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto p-6 space-y-4" onClick={e => e.stopPropagation()} data-testid="weekend-prefs-dialog">
        <div className="flex items-center justify-between">
          <h2 className="font-heading text-xl font-bold">Weekend Preferences</h2>
          <button onClick={onClose} className="p-1 text-muted-foreground hover:text-foreground"><X size={18} /></button>
        </div>

        <div>
          <label className="text-xs font-medium text-muted-foreground">Family brief for the AI <span className="font-normal">(most powerful — write anything)</span></label>
          <textarea value={form.ai_brief} onChange={e => setForm({ ...form, ai_brief: e.target.value })} rows={4} maxLength={1500}
            placeholder="e.g. We prefer half-day outings. Girls sleep by 9:30 PM. Avoid crowded malls. Pure veg restaurants only. Love beach walks. Grandma joins sometimes so easy walking preferred..."
            data-testid="ai-brief-input" className="w-full mt-1 px-3 py-2 rounded-lg border border-border bg-background text-sm resize-none" />
        </div>

        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="text-xs font-medium text-muted-foreground">Max outings per weekend</label>
            <select value={form.max_outings} onChange={e => setForm({ ...form, max_outings: e.target.value })} data-testid="max-outings-select"
              className="w-full mt-1 px-3 py-2 rounded-lg border border-border bg-background text-sm">
              <option value={0}>No limit</option><option value={1}>1 outing</option><option value={2}>2 outings</option>
            </select>
          </div>
          <div>
            <label className="text-xs font-medium text-muted-foreground">Nothing before</label>
            <input type="time" value={form.preferred_start} onChange={e => setForm({ ...form, preferred_start: e.target.value })} data-testid="preferred-start-input"
              className="w-full mt-1 px-3 py-2 rounded-lg border border-border bg-background text-sm" />
          </div>
          <div>
            <label className="text-xs font-medium text-muted-foreground">Travel radius</label>
            <select value={form.travel_radius} onChange={e => setForm({ ...form, travel_radius: e.target.value })} data-testid="travel-radius-select"
              className="w-full mt-1 px-3 py-2 rounded-lg border border-border bg-background text-sm">
              <option value="anywhere">Anywhere in Mumbai</option>
              <option value="south_mumbai">South Mumbai only</option>
              <option value="custom">Custom…</option>
            </select>
          </div>
          <div className="flex items-end pb-2">
            <label className="flex items-center gap-2 text-sm cursor-pointer" data-testid="eating-out-toggle">
              <input type="checkbox" checked={form.eating_out_ok} onChange={e => setForm({ ...form, eating_out_ok: e.target.checked })} className="accent-[#FF6B35]" />
              Eating out is OK
            </label>
          </div>
        </div>
        {form.travel_radius === 'custom' && (
          <input value={form.travel_custom} onChange={e => setForm({ ...form, travel_custom: e.target.value })}
            placeholder="e.g. within 30 min drive, western suburbs OK" className="w-full px-3 py-2 rounded-lg border border-border bg-background text-sm" />
        )}

        <VenueChips label="⭐ Favourite places (suggest sometimes)" color="bg-emerald-500/10 text-emerald-600" list={favs} setList={setFavs}
          placeholder="e.g. Marine Drive" testid="fav-venue-input" />
        <VenueChips label="🚫 Never suggest" color="bg-red-500/10 text-red-500" list={blocked} setList={setBlocked}
          placeholder="e.g. Phoenix Mall" testid="blocked-venue-input" />

        <div className="grid grid-cols-2 gap-3 pt-1 border-t border-border">
          <div>
            <label className="text-xs font-medium text-muted-foreground">Auto-plan day</label>
            <select value={form.auto_day} onChange={e => setForm({ ...form, auto_day: e.target.value })} data-testid="auto-day-select"
              className="w-full mt-1 px-3 py-2 rounded-lg border border-border bg-background text-sm">
              {DAYS.map(([k, l]) => <option key={k} value={k}>{l}</option>)}
            </select>
          </div>
          <div>
            <label className="text-xs font-medium text-muted-foreground">Auto-plan time</label>
            <select value={form.auto_hour} onChange={e => setForm({ ...form, auto_hour: e.target.value })} data-testid="auto-hour-select"
              className="w-full mt-1 px-3 py-2 rounded-lg border border-border bg-background text-sm">
              {Array.from({ length: 24 }, (_, h) => <option key={h} value={h}>{h === 0 ? '12 AM' : h < 12 ? `${h} AM` : h === 12 ? '12 PM' : `${h - 12} PM`}</option>)}
            </select>
          </div>
        </div>

        <label className="flex items-center gap-2 text-sm cursor-pointer" data-testid="surprise-monthly-toggle">
          <input type="checkbox" checked={form.surprise_monthly} onChange={e => setForm({ ...form, surprise_monthly: e.target.checked })} className="accent-pink-500" />
          🎲 First weekend of each month = automatic Surprise Weekend (never-done-before experiences)
        </label>

        <button onClick={save} disabled={saving} data-testid="save-prefs-btn"
          className="w-full py-2.5 rounded-full bg-[#FF6B35] text-white btn-glow text-sm font-semibold disabled:opacity-50">
          {saving ? 'Saving…' : 'Save Preferences'}
        </button>
      </div>
    </div>
  );
};
